import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { PERMISSIONS, defaultPermissions } from '../utils/permissions';

function UserManagementPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalMode, setUserModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userList, setUserList] = useState([]);  // ← ADD THIS LINE
  const [departments, setDepartments] = useState([]);   // the admin's departments (company setup) — "Reports to" uses them
  const [emailDomain, setEmailDomain] = useState(''); // Empty - fetch from backend

const [formData, setFormData] = useState({
    fullName: '',
    emailUsername: '',  // CHANGED: was 'email'
    designation: '',
    phone: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    password: '',
    confirmPassword: '',
    role: 'company_user',
    permissions: [],
    permissionsTouched: false,
    departmentId: ''
  });

  // ── Master Admin tabs (only rendered for master_admin role) ─────────────
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const [activeTab,      setActiveTab]      = useState('users');
  const [invoiceClients, setInvoiceClients] = useState([]);
  const [supportUsers,   setSupportUsers]   = useState([]);
  const [tabLoading,     setTabLoading]     = useState(false);
  const [tabMsg,         setTabMsg]         = useState('');

  // Edit invoice client state
  const [editingInvoice, setEditingInvoice] = useState(null);   // company object being edited
  const [invoiceEditForm, setInvoiceEditForm] = useState({});

  // Reset password state
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [newPassword,          setNewPassword]          = useState('');

  const fetchInvoiceClients = async () => {
    setTabLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/invoice-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (res.ok) setInvoiceClients(json.data || []);
    } catch(e) { console.error(e); }
    finally { setTabLoading(false); }
  };

  const fetchSupportUsers = async () => {
    setTabLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/support-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (res.ok) setSupportUsers(json.data || []);
    } catch(e) { console.error(e); }
    finally { setTabLoading(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTabMsg('');
    if (tab === 'invoice'  && invoiceClients.length === 0) fetchInvoiceClients();
    if (tab === 'support'  && supportUsers.length   === 0) fetchSupportUsers();
  };

  const handleUpdateInvoiceClient = async (companyId) => {
    setTabMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/invoice-client/${companyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          invoice_phase1_rate:    invoiceEditForm.invoice_phase1_rate ? parseFloat(invoiceEditForm.invoice_phase1_rate) : null,
          invoice_phase2_rate:    invoiceEditForm.invoice_phase2_rate ? parseFloat(invoiceEditForm.invoice_phase2_rate) : null,
          invoice_phase3_rate:    invoiceEditForm.invoice_phase3_rate ? parseFloat(invoiceEditForm.invoice_phase3_rate) : null,
          invoice_contact_email:  invoiceEditForm.invoice_contact_email || null,
          invoice_notes:          invoiceEditForm.invoice_notes || null,
        }),
      });
      if (res.ok) {
        setTabMsg('✅ Rates updated');
        setEditingInvoice(null);
        fetchInvoiceClients();
      } else {
        const j = await res.json();
        setTabMsg('❌ ' + (j.detail || 'Update failed'));
      }
    } catch(e) { setTabMsg('❌ Connection error'); }
  };

  const handleToggleInvoiceActive = async (company) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/admin/invoice-client/${company.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !company.is_active }),
      });
      fetchInvoiceClients();
    } catch(e) { console.error(e); }
  };

  const handleToggleSupportActive = async (u) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/admin/support-user/${u.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      fetchSupportUsers();
    } catch(e) { console.error(e); }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/support-user/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_password: newPassword }),
      });
      if (res.ok) {
        setTabMsg('✅ Password reset');
        setResetPasswordUserId(null);
        setNewPassword('');
      }
    } catch(e) { setTabMsg('❌ Connection error'); }
  };

  const handleDeleteSupportUser = async (userId, name) => {
    if (!window.confirm(`Delete support user "${name}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/admin/support-user/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setTabMsg('✅ User deleted');
      fetchSupportUsers();
    } catch(e) { setTabMsg('❌ Connection error'); }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/');
        return;
      }
      
      // Get company_id from JWT token
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const companyId = tokenPayload.company_id;

      console.log('Fetching users for company:', companyId); // DEBUG
      
      const response = await fetch(API_ENDPOINTS.USERS_COMPANY(companyId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Fetch users response:', response.status); // DEBUG

      if (response.ok) {
        const result = await response.json();
        console.log('Users data:', result); // DEBUG
        
        if (result.data && Array.isArray(result.data)) {
          // Transform backend data to match frontend format
          const transformedUsers = result.data.map((u) => ({
            id: u.id,
            name: u.full_name,
            email: u.email,
            jdCount: 0, // Can be fetched separately if needed
            lastActivity: u.last_login ? 'recently' : 'never',
            status: u.is_active ? 'Active' : 'Inactive',
            isActive: u.is_active,
            role: u.role,
            designation: u.designation || 'N/A',
            phone: u.phone || '',
            timezone: u.timezone || 'America/New_York',
            permissions: u.permissions || [],
            departmentId: u.department_id || null
          }));
          setUserList(transformedUsers);
        }
      } else if (response.status === 401) {
        // Token expired - redirect to login
        localStorage.clear();
        navigate('/');
      } else {
        console.error('Failed to fetch users:', response.status);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      navigate('/');
      return;
    }

    // Get company domain from JWT token
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const userEmail = tokenPayload.email;
    
    if (userEmail) {
      const domain = userEmail.split('@')[1];
      setEmailDomain(domain);
    }

    // Set user data
    setUser({
      id: tokenPayload.user_id,
      email: tokenPayload.email,
      full_name: tokenPayload.full_name,
      role: tokenPayload.role
    });

    // Set company data from token (if available)
    if (tokenPayload.company_id) {
      setCompany({
        id: tokenPayload.company_id,
        company_name: tokenPayload.company_name || 'Company'
      });
    }
    
    // Fetch users from backend
    fetchUsers();
  }, [navigate, fetchUsers]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    navigate('/');
  };

 const handleGoBack = () => {
    if (user?.role === 'master_admin') {
      navigate('/dashboard/master-admin');
    } else if (user?.role === 'company_super_admin') {
      navigate('/dashboard/company-admin');
    } else {
      navigate('/dashboard/user');
    }
  };

  // User Management Handlers
  const openCreateUserModal = () => {
    setUserModalMode('create');
    setFormData({
      fullName: '',
      emailUsername: '',
      designation: '',
      phone: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      password: '',
      confirmPassword: '',
      role: 'company_user',
      permissions: [],
      permissionsTouched: false,
      departmentId: ''
    });
    setFormError('');
    setShowUserModal(true);
  };

  useEffect(() => {
    if (user?.role !== 'company_super_admin') return;
    fetch(`${API_BASE}/api/company-setup`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setDepartments((d && d.departments) || []))
      .catch(() => setDepartments([]));
  }, [user?.role, API_BASE]);

  const openEditUserModal = (userToEdit) => {
    setUserModalMode('edit');
    setSelectedUser(userToEdit);
    setFormData({
      fullName: userToEdit.name,
      emailUsername: userToEdit.email.split('@')[0], // Extract username part
      designation: userToEdit.designation,
      phone: userToEdit.phone || '',
      timezone: userToEdit.timezone || 'America/New_York',
      password: '',
      confirmPassword: '',
      role: userToEdit.role,
      permissions: userToEdit.permissions || [],
      permissionsTouched: true,
      departmentId: userToEdit.departmentId ? String(userToEdit.departmentId) : ''
    });
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setFormError('');
  };

  const validateUserForm = () => {
    if (!formData.fullName.trim()) {
      setFormError('Full name is required');
      return false;
    }
    if (!formData.emailUsername.trim()) {
      setFormError('Email username is required');
      return false;
    }
    if (!/^[a-zA-Z0-9._-]+$/.test(formData.emailUsername)) {
      setFormError('Email username can only contain letters, numbers, dots, hyphens, and underscores');
      return false;
    }
    if (!formData.designation.trim()) {
      setFormError('Designation is required');
      return false;
    }
    if (departments.length && formData.role === 'company_user' && !formData.departmentId) {
      setFormError('Department is required');
      return false;
    }
    if (!formData.phone.trim()) {
      setFormError('Phone is required');
      return false;
    }
    
    if (userModalMode === 'create') {
      if (!formData.password) {
        setFormError('Password is required');
        return false;
      }
      if (formData.password.length < 8) {
        setFormError('Password must be at least 8 characters');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setFormError('Passwords do not match');
        return false;
      }
    }
    
    return true;
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    if (!validateUserForm()) return;

    setIsLoading(true);
    setFormError('');

    try {
      if (userModalMode === 'create') {
        // Create new user
        const token = localStorage.getItem('token');
        
        // Get company_id from JWT
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const companyId = tokenPayload.company_id;
        
        // Build full email
        const fullEmail = `${formData.emailUsername}@${emailDomain}`;
        
        const response = await fetch(API_ENDPOINTS.USERS_COMPANY_CREATE(companyId), {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            username: formData.emailUsername,
            email: fullEmail,
            full_name: formData.fullName,
            designation: formData.designation,
            phone: formData.phone,
            timezone: formData.timezone || 'America/New_York',
            password: formData.password,
            role: formData.role,
            permissions: formData.role === 'company_user' ? formData.permissions : [],
            department_id: formData.departmentId ? Number(formData.departmentId) : null
          })
        });

        const result = await response.json();

        if (response.ok) {
          const newUser = {
            id: result.data.id,
            name: formData.fullName,
            email: formData.email,
            jdCount: 0,
            lastActivity: 'just now',
            status: 'Active',
            role: formData.role,
            designation: formData.designation
          };
          setUserList([...userList, newUser]);
          closeUserModal();
          // Refresh user list from backend
          fetchUsers();
          alert(`✅ User ${formData.fullName} created successfully!`);
        } else if (response.status === 401) {
          localStorage.clear();
          navigate('/');
        } else {
          setFormError(result.detail || 'Failed to create user');
        }
      } else {
        // Edit user
        const token = localStorage.getItem('token');
        
        const response = await fetch(API_ENDPOINTS.USERS_UPDATE(selectedUser.id), {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        body: JSON.stringify({
            email: `${formData.emailUsername}@${emailDomain}`,
            full_name: formData.fullName,
            designation: formData.designation,
            phone: formData.phone,
            timezone: formData.timezone || 'America/New_York',
            role: formData.role,
            ...(formData.role === 'company_user' ? { permissions: formData.permissions } : {}),
            ...(departments.length ? { department_id: formData.departmentId ? Number(formData.departmentId) : null } : {})
          })
        });

        const result = await response.json();

        if (response.ok) {
          closeUserModal();
          // Refresh full list from backend to get all updated fields
          fetchUsers();
          alert(`✅ User ${formData.fullName} updated successfully!`);
        } else if (response.status === 401) {
          localStorage.clear();
          navigate('/');
        } else {
          setFormError(result.detail || 'Failed to update user');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setFormError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userToDelete.name}? They can be reactivated later.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(API_ENDPOINTS.USERS_DELETE(userToDelete.id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert(`✅ User ${userToDelete.name} deactivated successfully!`);
        // Refresh the entire list from backend
        fetchUsers();
      } else if (response.status === 401) {
        localStorage.clear();
        navigate('/');
      } else {
        const result = await response.json();
        alert(`❌ Error: ${result.detail || 'Failed to deactivate user'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Connection error. Please try again.');
    }
  };

  const handleReactivateUser = async (userToReactivate) => {
    if (!window.confirm(`Are you sure you want to reactivate ${userToReactivate.name}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(API_ENDPOINTS.USERS_REACTIVATE(userToReactivate.id), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert(`✅ User ${userToReactivate.name} reactivated successfully!`);
        // Refresh the entire list from backend
        fetchUsers();
      } else if (response.status === 401) {
        localStorage.clear();
        navigate('/');
      } else {
        const result = await response.json();
        alert(`❌ Error: ${result.detail || 'Failed to reactivate user'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Connection error. Please try again.');
    }
  };

  const handlePermanentlyDeleteUser = async (userToDelete) => {
    const confirmOnce = window.confirm(
      `⚠️ WARNING: You are about to PERMANENTLY DELETE ${userToDelete.name}!\n\nThis action CANNOT be undone and all data will be lost.\n\nAre you sure?`
    );
    
    if (!confirmOnce) return;

    const confirmTwice = window.confirm(
      `🚨 FINAL WARNING: This will permanently delete ${userToDelete.name} (${userToDelete.email}) from the system.\n\nClick OK to confirm permanent deletion.`
    );
    
    if (!confirmTwice) return;

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(API_ENDPOINTS.USERS_DELETE_PERMANENT(userToDelete.id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert(`✅ User ${userToDelete.name} has been permanently deleted!`);
        // Refresh the entire list from backend
        fetchUsers();
      } else if (response.status === 401) {
        localStorage.clear();
        navigate('/');
      } else {
        const result = await response.json();
        alert(`❌ Error: ${result.detail || 'Failed to permanently delete user'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Connection error. Please try again.');
    }
  };

  const filteredUsers = userList.filter(u =>
    u.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
    u.email.toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          {/* Left - Logo & Title */}
          <div className="flex items-center gap-4">
            <button 
              onClick={handleGoBack}
              className="text-brand-blue hover:text-brand-blue/80 mr-2"
            >
              ← Back
            </button>
            <img 
              src="/Reqt5d-Logo.png" 
              alt="Reqt5d" 
              className="h-14"
            />
            <div>
              <p className="text-sm text-gray-600">Manage Team</p>
              <p className="text-lg font-semibold text-gray-900">{company?.company_name || 'Loading...'}</p>
            </div>
          </div>

          {/* Right - User Menu */}
          <div className="flex items-center gap-6">
            {/* User Avatar & Info */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-brand-blue to-brand-green rounded-full flex items-center justify-center text-white font-semibold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{user?.full_name || 'User'}</p>
                <p className="text-xs text-gray-600">{user?.email || ''}</p>
              </div>
            </div>

            {/* Menu Button */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-200">
                    ⚙️ Settings
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    🚪 Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="space-y-8">
          {/* Page Header */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {user?.role === 'master_admin' ? 'Platform Management' : 'Team Members'}
            </h1>
            <p className="text-gray-600 mt-1">
              {user?.role === 'master_admin' ? 'Manage invoice clients, support staff and company users' : 'Manage your team members and their access levels'}
            </p>
          </div>

          {/* ── Tab Bar (master_admin only — company admins manage people and job boards in Organization) ── */}
          {user?.role === 'master_admin' && (
            <div className="flex gap-1 bg-white rounded-xl border border-gray-200 shadow-sm p-1 w-fit">
              {[
                { key: 'users',   label: '👥 Company Users' },
                { key: 'invoice', label: '🏢 Invoice Clients' },
                { key: 'support', label: '🎧 Support Users' },
              ].map(t => (
                <button key={t.key} onClick={() => handleTabChange(t.key)}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === t.key
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Tab message */}
          {tabMsg && (
            <div className={`px-4 py-3 rounded-xl text-sm font-medium ${
              tabMsg.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200'
                                      : 'bg-red-50 text-red-700 border border-red-200'
            }`}>{tabMsg}</div>
          )}

          {/* ═══════════════════════════════════════════════════════════
               TAB: Invoice Clients — master_admin only
          ═══════════════════════════════════════════════════════════ */}
          {user?.role === 'master_admin' && activeTab === 'invoice' && (
            <div>
              {tabLoading ? (
                <div className="p-10 text-center text-gray-400">Loading...</div>
              ) : invoiceClients.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
                  <div className="text-5xl mb-4">🏢</div>
                  <p className="text-gray-400">No invoice clients yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {invoiceClients.map(c => (
                    <div key={c.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${!c.is_active ? 'opacity-60' : 'border-gray-200'}`}>
                      <div className="flex items-start justify-between p-6">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-base font-bold text-gray-900">{c.company_name}</h3>
                            <span className="text-xs font-mono text-gray-400">{c.company_id}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {c.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          {c.super_admin && (
                            <p className="text-sm text-gray-500 mt-1">
                              Admin: <span className="font-medium">{c.super_admin.name}</span> · {c.super_admin.email}
                            </p>
                          )}
                          {c.invoice_notes && <p className="text-xs text-gray-400 italic mt-1">{c.invoice_notes}</p>}
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setEditingInvoice(c); setInvoiceEditForm({
                            invoice_phase1_rate: c.invoice_phase1_rate || '',
                            invoice_phase2_rate: c.invoice_phase2_rate || '',
                            invoice_phase3_rate: c.invoice_phase3_rate || '',
                            invoice_contact_email: c.invoice_contact_email || '',
                            invoice_notes: c.invoice_notes || '',
                          }); }}
                            className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-lg transition-all">
                            ✏️ Edit Rates
                          </button>
                          <button onClick={() => handleToggleInvoiceActive(c)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                              c.is_active
                                ? 'bg-red-50 hover:bg-red-100 text-red-600'
                                : 'bg-green-50 hover:bg-green-100 text-green-700'
                            }`}>
                            {c.is_active ? '⛔ Deactivate' : '✅ Activate'}
                          </button>
                        </div>
                      </div>

                      {/* Rates row */}
                      <div className="grid grid-cols-3 gap-0 border-t border-gray-100">
                        {[
                          { label: 'Phase 1 — JD',       rate: c.invoice_phase1_rate, color: 'blue' },
                          { label: 'Phase 2 — Intake',   rate: c.invoice_phase2_rate, color: 'purple' },
                          { label: 'Phase 3 — Interview',rate: c.invoice_phase3_rate, color: 'green' },
                        ].map((ph, i) => (
                          <div key={i} className={`p-4 text-center ${i < 2 ? 'border-r border-gray-100' : ''}`}>
                            <p className="text-xs text-gray-400 font-medium">{ph.label}</p>
                            <p className={`text-xl font-bold mt-1 ${ph.rate ? 'text-gray-800' : 'text-gray-300'}`}>
                              {ph.rate ? `$${ph.rate}` : '—'}
                            </p>
                            <p className="text-xs text-gray-400">per use</p>
                          </div>
                        ))}
                      </div>

                      {/* Inline edit form */}
                      {editingInvoice?.id === c.id && (
                        <div className="border-t border-purple-100 bg-purple-50 p-5">
                          <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-3">Edit Rates & Details</p>
                          <div className="grid grid-cols-3 gap-3 mb-3">
                            {['invoice_phase1_rate','invoice_phase2_rate','invoice_phase3_rate'].map((k, i) => (
                              <div key={k}>
                                <label className="text-xs text-gray-500 mb-1 block">Phase {i+1} Rate ($)</label>
                                <input type="number" step="0.01" min="0" value={invoiceEditForm[k] || ''}
                                  onChange={e => setInvoiceEditForm(f => ({...f, [k]: e.target.value}))}
                                  className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm bg-white focus:outline-none focus:border-purple-500" />
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Invoice Contact Email</label>
                              <input type="email" value={invoiceEditForm.invoice_contact_email || ''}
                                onChange={e => setInvoiceEditForm(f => ({...f, invoice_contact_email: e.target.value}))}
                                className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm bg-white focus:outline-none focus:border-purple-500" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Agreement Notes</label>
                              <input value={invoiceEditForm.invoice_notes || ''}
                                onChange={e => setInvoiceEditForm(f => ({...f, invoice_notes: e.target.value}))}
                                className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm bg-white focus:outline-none focus:border-purple-500" />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleUpdateInvoiceClient(c.id)}
                              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg transition-all">
                              Save Changes
                            </button>
                            <button onClick={() => setEditingInvoice(null)}
                              className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg transition-all border border-gray-200">
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════
               TAB: Support Users — master_admin only
          ═══════════════════════════════════════════════════════════ */}
          {user?.role === 'master_admin' && activeTab === 'support' && (
            <div>
              {tabLoading ? (
                <div className="p-10 text-center text-gray-400">Loading...</div>
              ) : supportUsers.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
                  <div className="text-5xl mb-4">🎧</div>
                  <p className="text-gray-400">No support users yet. Create one from the Master Admin dashboard.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Username</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Last Login</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportUsers.map(u => (
                        <tr key={u.id} className={`border-b border-gray-100 ${!u.is_active ? 'opacity-60 bg-gray-50' : 'hover:bg-gray-50'}`}>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">{u.full_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                          <td className="px-6 py-4 text-sm font-mono text-gray-500">{u.username}</td>
                          <td className="px-6 py-4 text-sm text-gray-400">
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button onClick={() => handleToggleSupportActive(u)}
                                className={`text-xs font-medium px-2 py-1 rounded-lg transition-all ${u.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}>
                                {u.is_active ? '⛔ Deactivate' : '✅ Activate'}
                              </button>
                              <button onClick={() => { setResetPasswordUserId(u.id); setNewPassword(''); }}
                                className="text-xs font-medium text-purple-600 hover:bg-purple-50 px-2 py-1 rounded-lg transition-all">
                                🔑 Reset Password
                              </button>
                              <button onClick={() => handleDeleteSupportUser(u.id, u.full_name)}
                                className="text-xs font-medium text-red-500 hover:bg-red-50 px-2 py-1 rounded-lg transition-all">
                                🗑️ Delete
                              </button>
                            </div>
                            {/* Inline password reset */}
                            {resetPasswordUserId === u.id && (
                              <div className="flex gap-2 mt-2">
                                <input type="password" placeholder="New password" value={newPassword}
                                  onChange={e => setNewPassword(e.target.value)}
                                  className="px-3 py-1.5 border border-purple-200 rounded-lg text-xs focus:outline-none focus:border-purple-500 w-36" />
                                <button onClick={() => handleResetPassword(u.id)}
                                  className="px-3 py-1.5 bg-purple-600 text-white text-xs font-bold rounded-lg">
                                  Save
                                </button>
                                <button onClick={() => setResetPasswordUserId(null)}
                                  className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-lg">
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════
               TAB: Company Users — ALL roles (existing content)
               Hidden when another tab is active (admins only can switch tabs)
          ═══════════════════════════════════════════════════════════ */}
          {activeTab === 'users' && (
            <div className="space-y-8">

          {/* Summary Stats */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Team Members</p>
                  <p className="text-4xl font-bold text-brand-blue mt-2">{userList.length}</p>
                </div>
                <span className="text-5xl">👥</span>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Super Admins</p>
                  <p className="text-4xl font-bold text-brand-green mt-2">
                    {userList.filter(u => u.role === 'company_super_admin').length}
                  </p>
                </div>
                <span className="text-5xl">👑</span>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Users</h2>
                <button 
                  onClick={openCreateUserModal}
                  className="px-4 py-2 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors text-sm font-medium"
                >
                  + Add User
                </button>
              </div>
              
              {/* Search Filter */}
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-brand-blue focus:outline-none text-sm"
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Designation</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Last Activity</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className={`border-b border-gray-200 transition-colors ${
                      u.isActive ? 'hover:bg-gray-50' : 'bg-gray-100 opacity-60'
                    }`}>
                      <td className={`px-6 py-4 text-sm font-medium ${u.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                        {u.name}
                        {u.timezone && (
                          <span className="block text-xs text-gray-400 font-normal mt-0.5">🌐 {u.timezone.split('/').pop().replace(/_/g, ' ')}</span>
                        )}
                      </td>
                      <td className={`px-6 py-4 text-sm ${u.isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                        {u.email}
                      </td>
                      <td className={`px-6 py-4 text-sm ${u.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                        {u.designation}
                        {u.role !== 'company_super_admin' && (u.permissions || []).length > 0 && (
                          <span className="flex flex-wrap gap-1 mt-1" data-testid={`perm-tags-${u.id}`}>
                            {PERMISSIONS.filter((p) => u.permissions.includes(p.key)).map((p) => (
                              <span key={p.key} className="px-2 py-0.5 rounded-full bg-blue-50 text-brand-dark text-[11px] font-semibold">{p.label}</span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          u.role === 'company_super_admin' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
                        }`}>
                          {u.role === 'company_super_admin' ? '👑 Super Admin' : '👤 User'}
                        </span>
                      </td>
                      <td className={`px-6 py-4 text-sm ${u.isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                        {u.lastActivity}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          u.isActive 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-gray-300 text-gray-700'
                        }`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm space-x-3">
                        {u.isActive ? (
                          <>
                            <button 
                              onClick={() => openEditUserModal(u)}
                              className="text-brand-blue hover:text-brand-blue/80 font-medium"
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteUser(u)}
                              className="text-orange-600 hover:text-orange-800 font-medium"
                            >
                              ⛔ Deactivate
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              onClick={() => handleReactivateUser(u)}
                              className="text-green-600 hover:text-green-800 font-medium"
                            >
                              ♻️ Reactivate
                            </button>
                            <button 
                              onClick={() => handlePermanentlyDeleteUser(u)}
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              🗑️ Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="p-6 text-center text-gray-600">
                <p>No users found matching "{searchFilter}"</p>
              </div>
            )}
          </div>
          </div>
          )}{/* end company users tab conditional */}

        </div>
      </main>

      {/* User Management Modal - IMPROVED UI */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-brand-blue to-blue-600">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span className="text-xl">{userModalMode === 'create' ? '➕' : '✏️'}</span>
                  {userModalMode === 'create' ? 'Add New Team Member' : 'Edit Team Member'}
                </h2>
                <button 
                  onClick={closeUserModal}
                  className="text-white hover:text-gray-200 text-2xl font-light leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveUser} className="p-6">
              {/* Error Alert */}
              {formError && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded text-sm">
                  <span className="font-medium">Error:</span> {formError}
                </div>
              )}

              <div className="space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
                    placeholder="Enter full name"
                    required
                  />
                </div>

                {/* Email - Split Design */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-brand-blue/20 focus-within:border-brand-blue transition-all">
                    <input
                      type="text"
                      value={formData.emailUsername}
                      onChange={(e) => setFormData({ ...formData, emailUsername: e.target.value })}
                      className="flex-1 bg-transparent text-sm focus:outline-none"
                      placeholder="username"
                      required
                      disabled={userModalMode === 'edit'}
                    />
                    <span className="text-sm text-gray-500 font-medium">@{emailDomain}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.emailUsername ? `${formData.emailUsername}@${emailDomain}` : 'Enter username for email'}
                  </p>
                </div>

                {/* Designation Dropdown */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value,
                      // a new person starts with what the designation usually means, until the admin changes a box
                      ...(formData.permissionsTouched ? {} : { permissions: defaultPermissions(e.target.value) }) })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
                    required
                  >
                    <option value="">Select Designation</option>
                    <option value="Hiring Manager">Hiring Manager</option>
                    <option value="Recruitment Manager">Recruitment Manager</option>
                    <option value="Recruitment Lead">Recruitment Lead</option>
                    <option value="Senior Recruiter">Senior Recruiter</option>
                    <option value="Recruiter">Recruiter</option>
                    <option value="Sourcing Recruiter">Sourcing Recruiter</option>
                    <option value="Interviewer">Interviewer</option>
                  </select>
                </div>

                {/* Department — from the company's departments (Company setup); required for company users */}
                {departments.length > 0 ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Department {formData.role === 'company_user' && <span className="text-red-500">*</span>}
                    </label>
                    <select aria-label="Department" value={formData.departmentId}
                      onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all">
                      <option value="">Select Department</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                ) : user?.role === 'company_super_admin' && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="no-departments">
                    No departments yet — add them in Company setup so hiring managers can pick who a job reports to.
                  </p>
                )}

                {/* Phone */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
                    placeholder="+1 (555) 000-0000"
                    required
                  />
                </div>

                {/* Timezone */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Timezone <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
                    required
                  >
                    <option value="">Select Timezone</option>
                    <optgroup label="🇺🇸 United States">
                      <option value="America/New_York">Eastern Time (EST/EDT)</option>
                      <option value="America/Chicago">Central Time (CST/CDT)</option>
                      <option value="America/Denver">Mountain Time (MST/MDT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                      <option value="America/Anchorage">Alaska (AKST/AKDT)</option>
                      <option value="Pacific/Honolulu">Hawaii (HST)</option>
                    </optgroup>
                    <optgroup label="🇮🇳 India">
                      <option value="Asia/Kolkata">India Standard Time (IST)</option>
                    </optgroup>
                    <optgroup label="🇬🇧 United Kingdom">
                      <option value="Europe/London">UK Time (GMT/BST)</option>
                    </optgroup>
                    <optgroup label="🇪🇺 Europe">
                      <option value="Europe/Berlin">Central European (CET/CEST)</option>
                      <option value="Europe/Paris">France (CET/CEST)</option>
                      <option value="Europe/Amsterdam">Netherlands (CET/CEST)</option>
                      <option value="Europe/Bucharest">Eastern European (EET/EEST)</option>
                    </optgroup>
                    <optgroup label="🇨🇦 Canada">
                      <option value="America/Toronto">Eastern (EST/EDT)</option>
                      <option value="America/Vancouver">Pacific (PST/PDT)</option>
                      <option value="America/Edmonton">Mountain (MST/MDT)</option>
                    </optgroup>
                    <optgroup label="🌏 Asia Pacific">
                      <option value="Asia/Dubai">UAE / Gulf (GST)</option>
                      <option value="Asia/Singapore">Singapore (SGT)</option>
                      <option value="Asia/Tokyo">Japan (JST)</option>
                      <option value="Asia/Shanghai">China (CST)</option>
                      <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                      <option value="Asia/Seoul">South Korea (KST)</option>
                      <option value="Asia/Manila">Philippines (PHT)</option>
                      <option value="Asia/Kuala_Lumpur">Malaysia (MYT)</option>
                      <option value="Asia/Jakarta">Indonesia Western (WIB)</option>
                    </optgroup>
                    <optgroup label="🇦🇺 Australia / NZ">
                      <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                      <option value="Australia/Melbourne">Melbourne (AEST/AEDT)</option>
                      <option value="Australia/Perth">Perth (AWST)</option>
                      <option value="Pacific/Auckland">New Zealand (NZST/NZDT)</option>
                    </optgroup>
                    <optgroup label="🌎 Latin America">
                      <option value="America/Mexico_City">Mexico City (CST/CDT)</option>
                      <option value="America/Sao_Paulo">São Paulo (BRT)</option>
                      <option value="America/Argentina/Buenos_Aires">Buenos Aires (ART)</option>
                      <option value="America/Bogota">Colombia (COT)</option>
                    </optgroup>
                    <optgroup label="🌍 Africa / Middle East">
                      <option value="Africa/Johannesburg">South Africa (SAST)</option>
                      <option value="Africa/Lagos">Nigeria / West Africa (WAT)</option>
                      <option value="Africa/Nairobi">East Africa (EAT)</option>
                      <option value="Asia/Riyadh">Saudi Arabia (AST)</option>
                      <option value="Asia/Jerusalem">Israel (IST/IDT)</option>
                    </optgroup>
                  </select>
                  {formData.timezone && (
                    <p className="text-xs text-blue-600 mt-1">
                      🕐 Current time: {new Date().toLocaleTimeString('en-US', { timeZone: formData.timezone, hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short' })}
                    </p>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
                  >
                    <option value="company_user">👤 Company User</option>
                    <option value="company_super_admin">👑 Super Admin</option>
                  </select>
                </div>

                {/* Permissions — what this person can do (the server checks every one) */}
                <fieldset>
                  <legend className="block text-xs font-medium text-gray-700 mb-1.5">Permissions</legend>
                  {formData.role === 'company_super_admin' ? (
                    <p className="text-xs text-gray-500">Company admins have every permission.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {PERMISSIONS.map((p) => (
                        <label key={p.key} className="flex items-start gap-2 text-xs text-gray-700">
                          <input type="checkbox" className="mt-0.5" checked={formData.permissions.includes(p.key)}
                            onChange={(e) => setFormData({ ...formData, permissionsTouched: true,
                              permissions: e.target.checked ? [...formData.permissions, p.key] : formData.permissions.filter((k) => k !== p.key) })} />
                          <span><span className="font-semibold">{p.label}</span> <span className="text-gray-500">— {p.hint}</span></span>
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>

                {/* Password Fields - Only for Create Mode */}
                {userModalMode === 'create' && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-600 mb-3">Set Password</p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
                          placeholder="Minimum 8 characters"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
                          placeholder="Re-enter password"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeUserModal}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    userModalMode === 'create' ? '✓ Create User' : '✓ Update User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagementPage;